import { Node } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer, type ReactNodeViewProps } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";

/*
 * Formula (LaTeX, rendered by KaTeX) and Diagram (Mermaid) blocks. Both store their source as the
 * block's text, so the HTML stays readable and searchable without the app:
 *   <div data-math=""><code>E = mc^2</code></div>
 *   <div data-mermaid=""><pre>graph TD; A-->B</pre></div>
 * The renderers are loaded only when a note shows one of these blocks, so the main bundle doesn't grow.
 */

type Katex = typeof import("katex").default;
let katexP: Promise<Katex> | null = null;
export function loadKatex(): Promise<Katex> {
  katexP ??= Promise.all([import("katex"), import("katex/dist/katex.min.css")]).then(([m]) => m.default);
  return katexP;
}

type Mermaid = typeof import("mermaid").default;
let mermaidP: Promise<Mermaid> | null = null;
let mermaidTheme = "";
function isDark() {
  const t = document.documentElement.getAttribute("data-theme");
  return t ? t === "dark" : matchMedia("(prefers-color-scheme: dark)").matches;
}
export async function loadMermaid(): Promise<Mermaid> {
  mermaidP ??= import("mermaid").then((m) => m.default);
  const mermaid = await mermaidP;
  const theme = isDark() ? "dark" : "neutral";
  if (theme !== mermaidTheme) {
    mermaidTheme = theme;
    mermaid.initialize({ startOnLoad: false, securityLevel: "strict", theme, fontFamily: "Inter Variable, Inter, system-ui, sans-serif" });
  }
  return mermaid;
}

let seq = 0;
/** Mermaid source → SVG markup (sanitized by Mermaid's strict mode). Throws on a syntax error. */
export async function mermaidSvg(source: string): Promise<string> {
  const mermaid = await loadMermaid();
  const { svg } = await mermaid.render(`mmd-${Date.now().toString(36)}-${++seq}`, source);
  return svg;
}

export async function katexHtml(source: string, opts: { mathml?: boolean } = {}): Promise<string> {
  const katex = await loadKatex();
  return katex.renderToString(source, { displayMode: true, throwOnError: false, output: opts.mathml ? "mathml" : "htmlAndMathml", trust: false, strict: "ignore" });
}

const sourceAttr = (inner: string) => ({
  source: {
    default: "",
    parseHTML: (el: HTMLElement) => (el.querySelector(inner)?.textContent ?? el.textContent ?? "").replace(/\r\n?/g, "\n"),
    renderHTML: () => ({}),
  },
});

export const MathBlock = Node.create({
  name: "mathBlock",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return sourceAttr("code");
  },
  parseHTML() {
    return [{ tag: "div[data-math]" }];
  },
  renderHTML({ node }) {
    return ["div", { "data-math": "" }, ["code", {}, node.attrs.source as string]];
  },
  renderText({ node }) {
    return node.attrs.source as string;
  },
  addNodeView() {
    return ReactNodeViewRenderer((p: ReactNodeViewProps) => <SourceBlock {...p} kind="math" />);
  },
});

export const MermaidBlock = Node.create({
  name: "mermaidBlock",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return sourceAttr("pre");
  },
  parseHTML() {
    return [{ tag: "div[data-mermaid]" }];
  },
  renderHTML({ node }) {
    return ["div", { "data-mermaid": "" }, ["pre", {}, node.attrs.source as string]];
  },
  renderText({ node }) {
    return node.attrs.source as string;
  },
  addNodeView() {
    return ReactNodeViewRenderer((p: ReactNodeViewProps) => <SourceBlock {...p} kind="mermaid" />);
  },
});

const LABEL = { math: "Formula", mermaid: "Diagram" } as const;
const HELP = {
  math: "LaTeX, e.g. \\frac{a}{b} or \\sum_{i=1}^{n} i^2",
  mermaid: "Mermaid, e.g. graph TD; A-->B",
} as const;

function useRendered(kind: "math" | "mermaid", source: string) {
  const [out, setOut] = useState<{ html: string; error: string } | null>(null);
  useEffect(() => {
    if (!source.trim()) return setOut({ html: "", error: "" });
    let live = true;
    const t = setTimeout(
      () => {
        (kind === "math" ? katexHtml(source) : mermaidSvg(source))
          .then((html) => live && setOut({ html, error: "" }))
          .catch((e: unknown) => live && setOut((o) => ({ html: o?.html ?? "", error: e instanceof Error ? e.message.split("\n")[0]! : "Can't draw this" })));
      },
      out ? 250 : 0, // first paint at once; while typing, wait for a pause
    );
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [kind, source]); // eslint-disable-line react-hooks/exhaustive-deps
  return out;
}

function SourceBlock({ node, editor, updateAttributes, selected, kind }: ReactNodeViewProps & { kind: "math" | "mermaid" }) {
  const source = node.attrs.source as string;
  const [editing, setEditing] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const out = useRendered(kind, source);
  // A new, empty block opens straight into its editor.
  useEffect(() => {
    if (!source && editor.isEditable) setEditing(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (editing) area.current?.focus();
  }, [editing]);
  const done = () => {
    setEditing(false);
    editor.commands.focus();
  };
  return (
    <NodeViewWrapper className={`source-block ${kind}${selected ? " selected" : ""}${editing ? " editing" : ""}`}>
      <div
        className="source-render"
        contentEditable={false}
        title={editor.isEditable && !editing ? `Edit ${LABEL[kind].toLowerCase()}` : undefined}
        onClick={() => editor.isEditable && setEditing(true)}
      >
        {out === null ? (
          <span className="source-empty">Loading {LABEL[kind].toLowerCase()}…</span>
        ) : out.html ? (
          <div className="source-out" dangerouslySetInnerHTML={{ __html: out.html }} />
        ) : (
          <span className="source-empty">{out.error ? `${LABEL[kind]}: ${out.error}` : `Empty ${LABEL[kind].toLowerCase()} — click to write it`}</span>
        )}
      </div>
      {editing && (
        <div className="source-editor" contentEditable={false}>
          <textarea
            ref={area}
            spellCheck={false}
            value={source}
            placeholder={HELP[kind]}
            rows={Math.min(14, Math.max(kind === "math" ? 2 : 5, source.split("\n").length + 1))}
            onChange={(e) => updateAttributes({ source: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Escape" || (e.key === "Enter" && (e.ctrlKey || e.metaKey))) {
                e.preventDefault();
                done();
              }
            }}
          />
          <div className="source-bar">
            <span className={out?.error ? "source-error" : "muted"}>{out?.error || HELP[kind]}</span>
            <button className="btn small primary" onClick={done}>
              Done
            </button>
          </div>
        </div>
      )}
    </NodeViewWrapper>
  );
}

/**
 * HTML exports: each Formula/Diagram block gets its rendered form (MathML, or the diagram as an SVG
 * image) above its source, so the file shows it without the app. Runs in the browser before zipping.
 */
export async function renderBlocksForExport(html: string): Promise<string> {
  if (!/data-(math|mermaid)/.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const el of Array.from(doc.querySelectorAll<HTMLElement>("[data-math], [data-mermaid]"))) {
    const src = (el.querySelector("code, pre")?.textContent ?? "").trim();
    if (!src) continue;
    try {
      const wrap = doc.createElement("div");
      wrap.className = "rendered";
      if (el.hasAttribute("data-math")) {
        wrap.innerHTML = await katexHtml(src, { mathml: true });
      } else {
        const img = doc.createElement("img");
        img.alt = "Diagram";
        img.src = `data:image/svg+xml;base64,${toBase64(new TextEncoder().encode(await mermaidSvg(src)))}`;
        wrap.append(img);
      }
      el.prepend(wrap);
    } catch {
      /* keep the source only */
    }
  }
  return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}

function toBase64(d: Uint8Array): string {
  let s = "";
  for (let i = 0; i < d.length; i += 0x8000) s += String.fromCharCode(...d.subarray(i, i + 0x8000));
  return btoa(s);
}
